import { useRouter } from "next/navigation";

import rawContentfulLayoutData from "@/data/contentfulLayoutData.json";
import { TargetAttribute } from "@/types/common";
import { LayoutData } from "@/types/layoutData";
import { hrefLeavesPage, notifyNavigationStarted } from "@/utils/buttonNavigation";

/*
 * --- Module Scope ---
 * This runs once on load. It filters and maps the JSON into a Set for O(1) lookup.
 */
const internalNextPaths = new Set(
  (rawContentfulLayoutData as LayoutData[]).map((item) => item.url_slug)
);

export const useNavigation = () => {
  const router = useRouter();

  /**
   * @function navigate
   * @description Navigates to the specified path. It determines whether to use
   * Next.js's router or `window.open` based on the directly imported `contentfulLayoutData`.
   * @param {string} path - The URL path to navigate to. Can be an internal slug
   * or an external URL.
   */
  const navigate = (path: string) => {
    if (typeof path !== "string") {
      return;
    }

    // Separate path and search, Normalize: "test" -> "/test/"
    let stringPath = path.split(/[?#]/)[0].replace(/^\/+|\/+$/g, "");
    stringPath = stringPath === "" ? "/" : `/${stringPath}/`;

    const isInternal = internalNextPaths.has(stringPath);

    let externalPath: string = path;
    let target: TargetAttribute = "_top";
    const hasProtocol = (url: string) => /^(https?|tel|mailto):/i.test(url);
    const isBlog = path.includes("/blog/");

    // If the link is NOT isNextJs, construct the appropriate external path and target
    if (!isInternal) {
      if (hasProtocol(path)) {
        // Set target to "_blank" for external domains (not nutrafol.com, vercel.app, or localhost)
        if (
          !path.includes("nutrafol.com") &&
          !path.includes("vercel.app") &&
          !path.includes("localhost")
        ) {
          target = "_blank";
        }
      } else {
        // if has NO Protocol, prepend the base URL for external paths
        externalPath = process.env.NEXT_PUBLIC_BASEURL + path;
      }
    }

    if (isBlog) {
      target = "_blank";
    }
    // console.log("externalPath", externalPath);

    const leavesPage =
      typeof window !== "undefined" &&
      hrefLeavesPage(isInternal ? path : externalPath, window.location.href);

    if (isInternal) {
      if (leavesPage) notifyNavigationStarted();
      router.push(path);
    } else {
      if (leavesPage && target !== "_blank") notifyNavigationStarted();
      window.open(externalPath, target);
    }
  };

  return navigate;
};
